import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import { downloadFromUrl } from 'utils/download-blob';
import { subscribe } from 'lib/pusher';
import { armJobCompletion } from 'lib/hooks/useJobCompletion';
import type { DownloadResponse } from 'types/api-responses';
import type { Channel } from 'pusher-js';
import { factorGroupOrderAtom } from './atoms';
import type {
  AnalysisResponse,
  DrilldownWaterfallResponse,
  FactorGroupRow,
  PropertyExportRequest,
  ResultsRun,
  RunResponse,
  SlotListOrder,
  SlotReveal,
  SlotRowsPageResponse,
  WaterfallBar,
} from './types';
import {
  ANALYSIS_QUERY_KEY,
  ANALYSIS_TIMEOUT_MS,
  FACTOR_GROUPS,
  GROUP_FACTORS_QUERY_KEY,
  PROPERTY_EXPORT_MUTATION_KEY,
  PROPERTY_EXPORT_POLL,
  PUSHER_SUBSCRIBED_EVENT,
  RUN_QUERY_KEY,
  RUN_UPDATED_EVENT,
  SLOT_ROWS_QUERY_KEY,
  SLOT_WATERFALL_QUERY_KEY,
  isRunLive,
  reconcileFactorGroupOrder,
  drillQuery,
} from './utils';
import {
  SLOT_REVEAL_STEP,
  hasPageAfter,
  isSlotCoverageKey,
  revealSlotWaterfall,
} from './slot-reveal-utils';

/** The Origin/Target eval-3 ids the flow operates on (side A = Origin, B = Target). */
export interface EvalPair {
  origin: number;
  target: number;
}

/**
 * Reads the Origin/Target eval-3 pair from the URL query (`side_a_evaluation_id` /
 * `side_b_evaluation_id`) — the flow's single source of truth, carried forward from
 * the compare result page. Returns `null` unless both ids are present, integer, and
 * distinct, so callers get a clean pair-or-nothing to guard on (and TS narrows to
 * `number` ids inside the truthy branch).
 *
 * @return {EvalPair | null} The validated pair, or null when absent/invalid.
 */
export function useEvalPair(): EvalPair | null {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    const parseId = (key: string): number | null => {
      const raw = params.get(key);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isInteger(n) ? n : null;
    };
    const origin = parseId('side_a_evaluation_id');
    const target = parseId('side_b_evaluation_id');
    if (origin === null || target === null || origin === target) return null;
    return { origin, target };
  }, [search]);
}

/**
 * The comparison analysis, keyed by the eval-3 pair. Auto-runs once both ids are
 * present (the "Analyze Value Drivers" click already expressed intent) and is cached
 * per pair, so every step — Analysis and Factor Ordering — reads the same result
 * without re-fetching. It never goes stale: a pair analyzes the same way every time, so a
 * background refetch would only repeat a heavy request for the same answer. Disabled when an id
 * is missing.
 *
 * @param {number | null} originEvalId - The Origin EvaluationCalculation id (side A).
 * @param {number | null} targetEvalId - The Target EvaluationCalculation id (side B).
 * @return {UseQueryResult<AnalysisResponse, Error>} The React Query result.
 */
export function useComparisonAnalysis(
  originEvalId: number | null,
  targetEvalId: number | null
): UseQueryResult<AnalysisResponse, Error> {
  return useQuery<AnalysisResponse, Error>({
    queryKey: [ANALYSIS_QUERY_KEY, originEvalId, targetEvalId],
    queryFn: ({ signal }) =>
      api
        .post('packages/eval_comparison/analysis', {
          json: {
            side_a: { evaluation_calculation_id: originEvalId },
            side_b: { evaluation_calculation_id: targetEvalId },
          },
          // Override the api client's 30s default — the analysis is warehouse-heavy.
          timeout: ANALYSIS_TIMEOUT_MS,
          signal,
        })
        .json<AnalysisResponse>(),
    enabled: originEvalId !== null && targetEvalId !== null,
    staleTime: Infinity,
  });
}

/**
 * Reads the comparison `run_id` from the URL query — the Results step's source of
 * truth for which run to render. Returns null unless it is a positive integer, so a
 * bare deep link to Results (no run yet) is guarded.
 *
 * @return {number | null} The run id, or null when absent/invalid.
 */
export function useRunId(): number | null {
  const { search } = useLocation();
  return useMemo(() => {
    const raw = new URLSearchParams(search).get('run_id');
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [search]);
}

/**
 * Kicks off a comparison run (Step 4): queues it — the engine plans and fires its shadow evals
 * on Sidekiq — and returns it, still pending. The caller opens Results on the returned run id,
 * which follows the run from there. factor_order is the user's Step-3 ordering, in which the
 * engine attributes the changed factors.
 *
 * @return {UseMutationResult} The run mutation; call its mutate with the eval-3
 *   pair and the factor order to start the run.
 */
export function useCreateComparisonRun(): UseMutationResult<
  RunResponse,
  Error,
  { pair: EvalPair; factorOrder: string[] }
> {
  return useMutation<
    RunResponse,
    Error,
    { pair: EvalPair; factorOrder: string[] }
  >({
    mutationFn: ({ pair, factorOrder }) =>
      api
        .post('packages/eval_comparison/runs', {
          json: {
            side_a: { evaluation_calculation_id: pair.origin },
            side_b: { evaluation_calculation_id: pair.target },
            factor_order: factorOrder,
          },
        })
        .json<RunResponse>(),
  });
}

/**
 * Waits for a Download's file, which a job on the server builds. Every trigger — the subscription
 * going live, each event on the download's channel, the poll — re-reads the download, so a job
 * that finished before the subscription was live is still seen: the event is a hint, the read
 * decides (docs/pattern_catalog/patterns/push-notification.md). The channel is the download's
 * own, so this owns it, and unsubscribes once settled.
 *
 * @param {DownloadResponse} download - The download the POST answered.
 * @return {Promise<DownloadResponse>} The download once processed, its `fileUrl` set.
 * @throws {Error} When the download failed, a read of it answered a 4xx, or no read found it
 *   finished in time — queued past `queuedMs`, or running past `runningMs` (`PROPERTY_EXPORT_POLL`).
 */
function awaitDownload(download: DownloadResponse): Promise<DownloadResponse> {
  return new Promise((resolve, reject) => {
    // `lib/pusher` is untyped JavaScript; its `subscribe` hands back a pusher-js Channel.
    const subscription = subscribe(download.notifications.channel) as Channel;
    let settled = false;
    /**
     * Ends the wait once: disarms the triggers, leaves the channel, then resolves or rejects.
     * Every settle comes from a Pusher event, a poll timer or an awaited read, so `disarm` is
     * always assigned by the time this runs.
     *
     * @param {Function} finish - Resolves or rejects the wait.
     */
    const settle = (finish: () => void) => {
      if (settled) return;
      settled = true;
      disarm();
      subscription.unsubscribe();
      finish();
    };

    const startedAt = Date.now();
    // When a read first found the job running; until then it's waiting in the queue.
    let runningSince: number | null = null;
    const { queuedMs, runningMs, ...poll } = PROPERTY_EXPORT_POLL;

    /**
     * Reads the download, and settles the wait on what it finds: its file, its failure, or — past
     * the time its queue or its run allows — giving up. A 4xx won't change by asking again (the
     * download is gone, or isn't the user's), so it fails the wait at once; anything else (a
     * network blip, a 5xx, or a 408/429, which ask to come back later) is left for the next
     * trigger.
     *
     * @return {Promise<void>} Resolves once the read is handled.
     * @throws {Error} When the read fails in a way worth asking again.
     */
    const reconcile = async () => {
      let current: DownloadResponse;
      try {
        // `api` takes paths without a leading slash (its prefixUrl is set).
        current = await api
          .get(download.href.replace(/^\//, ''))
          .json<DownloadResponse>();
      } catch (error) {
        const final =
          error instanceof HTTPError &&
          error.response.status < 500 &&
          ![408, 429].includes(error.response.status);
        if (final) {
          settle(() => reject(new Error("The export couldn't be read.")));
          return;
        }
        throw error;
      }
      if (current.status === 'processed') {
        settle(() => resolve(current));
        return;
      }
      if (current.status === 'failed') {
        settle(() => reject(new Error('The export failed.')));
        return;
      }
      const now = Date.now();
      if (current.status === 'processing') runningSince ??= now;
      const overdue =
        runningSince === null
          ? now - startedAt > queuedMs
          : now - runningSince > runningMs;
      if (overdue) settle(() => reject(new Error('The export took too long.')));
    };

    const disarm = armJobCompletion({
      channel: subscription,
      events: download.notifications.events.map((event) => event.name),
      reconcile,
      poll: {
        ...poll,
        // A backstop only: the reads above decide, by where the export is.
        maxMs: queuedMs + runningMs,
        onExpired: () =>
          settle(() => reject(new Error('The export took too long.'))),
      },
    });
  });
}

/**
 * Exports a completed run's slot changes as one CSV, a row per property with a column per factor
 * (INS-12790). The POST starts a job on the server, which builds the file from the same slot lists
 * the page reads, scoped to the reserve category asked for; the mutation then waits for it
 * (`awaitDownload`) and saves it under the name the server gave it. The wait and the save are
 * part of the mutation itself, not a component effect or a callback passed to `mutate`, so the
 * file still arrives if the user leaves the Results page while it's being built.
 *
 * @return {UseMutationResult} The export mutation; call its mutate with the run id and the
 *   reserve category the shown results carry.
 */
export function useExportByProperty(): UseMutationResult<
  DownloadResponse,
  Error,
  PropertyExportRequest
> {
  return useMutation<DownloadResponse, Error, PropertyExportRequest>({
    mutationKey: [PROPERTY_EXPORT_MUTATION_KEY],
    // Kept until dismissed, not dropped once no button has watched it for React Query's 5
    // minutes: the user may come back to Results long after the export failed, and its failure
    // is read from here (`useExportByPropertyFailure`). A finished export is a few bytes.
    gcTime: Infinity,
    mutationFn: async ({ runId, rescat }) => {
      const download = await api
        .post(`packages/eval_comparison/runs/${runId}/property_exports`, {
          json: { rescat },
        })
        .json<DownloadResponse>();
      return awaitDownload(download);
    },
    onSuccess: (download) => downloadFromUrl(download.fileUrl),
  });
}

/**
 * Whether an Export by property of this run and category is being built — by any button,
 * including one the user has since left (the export keeps running, and saves its file, after its
 * button unmounts), so coming back to Results can't start a duplicate.
 *
 * @param {number} runId - The comparison run.
 * @param {string | null} rescat - The reserve category (null = all).
 * @return {boolean} True while that export is in flight.
 */
export function useIsExportingByProperty(
  runId: number,
  rescat: string | null
): boolean {
  // The filters name no prop: `useMutationState` takes new filters only at the cache's next
  // change, so a run or category passed in would lag a switch. They're matched here, at render.
  const pending = useMutationState({
    filters: { mutationKey: [PROPERTY_EXPORT_MUTATION_KEY], status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PropertyExportRequest | undefined,
  });
  return pending.some(
    (variables) => variables?.runId === runId && variables.rescat === rescat
  );
}

/**
 * A failed Export by property of this run, if any, and a way to dismiss it. It reads React Query's
 * mutation cache rather than one button's mutation, so the failure shows wherever the export was
 * started from: a button the user has since left (the export kept running after it unmounted), or
 * one for another category. Each category's latest export decides — so exporting it again clears
 * its failure — and of the categories whose latest export failed, the one started last is told.
 * Dismissing clears only that one, so the next shows in its place.
 *
 * @param {number} runId - The comparison run.
 * @return {object} `failure` — the failed export's category (null = all), or null when none
 *   failed — and `dismiss`, which clears that category's failure.
 */
export function useExportByPropertyFailure(runId: number): {
  failure: { rescat: string | null } | null;
  dismiss: () => void;
} {
  const queryClient = useQueryClient();
  // Filters free of props, as in `useIsExportingByProperty`: the run is matched at render.
  const exports = useMutationState({
    filters: { mutationKey: [PROPERTY_EXPORT_MUTATION_KEY] },
    select: (mutation) => ({
      status: mutation.state.status,
      variables: mutation.state.variables as PropertyExportRequest | undefined,
    }),
  });

  // The cache lists exports in the order they started: re-inserting keeps each category at its
  // latest.
  const latestByCategory = new Map<string | null, string>();
  exports.forEach(({ status, variables }) => {
    if (variables?.runId !== runId) return;
    latestByCategory.delete(variables.rescat);
    latestByCategory.set(variables.rescat, status);
  });
  const failed = [...latestByCategory].filter(
    ([, status]) => status === 'error'
  );
  const told = failed.at(-1);
  const failure = told ? { rescat: told[0] } : null;

  return {
    failure,
    dismiss: () => {
      if (!failure) return;
      const cache = queryClient.getMutationCache();
      cache
        .findAll({
          mutationKey: [PROPERTY_EXPORT_MUTATION_KEY],
          status: 'error',
        })
        .filter((mutation) => {
          const variables = mutation.state.variables as
            PropertyExportRequest | undefined;
          return (
            variables?.runId === runId && variables.rescat === failure.rescat
          );
        })
        .forEach((mutation) => cache.remove(mutation));
    },
  };
}

/**
 * React Query options for a comparison run, keyed by (run, reserve category) — the one
 * definition shared by the live run query and the Results page's fallback observer, so both
 * address the same cache entry. Enabled only when a run id is present.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {string | null} rescat - The reserve category to scope to, or null for all.
 * @return {object} The React Query options (queryKey, queryFn, enabled).
 */
export function runQueryOptions(runId: number | null, rescat: string | null) {
  return {
    queryKey: [RUN_QUERY_KEY, runId, rescat],
    queryFn: ({ signal }: QueryFunctionContext) =>
      api
        .get(
          `packages/eval_comparison/runs/${runId}${drillQuery(rescat, false)}`,
          {
            signal,
          }
        )
        .json<RunResponse>(),
    enabled: runId !== null,
  };
}

/**
 * Keeps a still-advancing run live: subscribes to its Pusher channel and, on every
 * `run_updated` event, refreshes the run and its drill-downs, so an open drill or an expanded
 * table group never lags the overview. Only the category on screen is re-read; every other one
 * the run was viewed in is marked stale, so it's re-read when it's shown again rather than
 * reassembled on every push. It also refetches once the subscription is live — Pusher doesn't
 * replay, so a transition pushed between the read that found the run live and the subscription
 * (a run that ends right after planning pushes only once) is reconciled rather than lost. Torn
 * down once the run is terminal. Driven by the run actually on screen, so the subscription holds
 * even while a fallback category is shown.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {RunResponse | null | undefined} run - The run on screen (ignored unless its id is `runId`).
 */
export function useRunUpdates(
  runId: number | null,
  run: RunResponse | null | undefined
): void {
  const queryClient = useQueryClient();
  const current = run && run.id === runId ? run : null;
  const channel = current?.pusherChannel ?? null;
  const isTerminal = current !== null && !isRunLive(current.status);

  useEffect(() => {
    if (runId === null || channel === null || isTerminal) return undefined;

    const subscription = subscribe(channel);
    const refetch = () => {
      queryClient.invalidateQueries({
        queryKey: [RUN_QUERY_KEY, runId],
        refetchType: 'active',
      });
      [
        GROUP_FACTORS_QUERY_KEY,
        SLOT_WATERFALL_QUERY_KEY,
        SLOT_ROWS_QUERY_KEY,
      ].forEach((root) =>
        queryClient.invalidateQueries({ queryKey: [root, runId] })
      );
    };
    subscription.bind(RUN_UPDATED_EVENT, refetch);
    subscription.bind(PUSHER_SUBSCRIBED_EVENT, refetch);

    return () => {
      subscription.unbind(RUN_UPDATED_EVENT, refetch);
      subscription.unbind(PUSHER_SUBSCRIBED_EVENT, refetch);
      subscription.unsubscribe();
    };
  }, [runId, channel, isTerminal, queryClient]);
}

/**
 * A comparison run, keyed by its id and reserve category, with the waterfall assembled
 * fresh by the BE on each read (scoped to `rescat`'s slots when set). By default it keeps the
 * run live while it advances ({@link useRunUpdates}). Disabled until a run id is present.
 *
 * Switching category keeps showing the previous category's data while the new one loads
 * (`keepPreviousData`), so the page doesn't drop to a spinner. That previous data can
 * belong to a different run after a `run_id` change — callers must check `data.id`.
 *
 * @param {number | null} runId - The comparison run id (from {@link useRunId}).
 * @param {string | null} [rescat] - The reserve category to scope to, or null for all.
 * @param {object} [options] - Hook options.
 * @param {boolean} [options.subscribe] - Keep the run live via Pusher (default true); off when
 *   the caller drives {@link useRunUpdates} itself.
 * @return {UseQueryResult<RunResponse, Error>} The React Query result.
 */
export function useComparisonRun(
  runId: number | null,
  rescat: string | null = null,
  { subscribe: live = true }: { subscribe?: boolean } = {}
): UseQueryResult<RunResponse, Error> {
  const query = useQuery<RunResponse, Error>({
    ...runQueryOptions(runId, rescat),
    placeholderData: keepPreviousData,
  });
  useRunUpdates(runId, live ? query.data : null);
  return query;
}

/**
 * The Results page's run and reserve-category filter. The category belongs to one run: a new
 * run id starts at All Rescats. It persists across drilling and tab switches.
 *
 * Which run is shown:
 * - the selected category's data once loaded, or the previous category's while it loads
 *   (`keepPreviousData`) — never another run's;
 * - when the switch failed, the category that was on screen when it was chosen. That fallback
 *   is observed — so it stays cached and reacts to updates — and only fetched (refreshed on run
 *   updates) while it's the one shown;
 * - a switch that follows a failed one is bridged by React Query itself: `keepPreviousData`
 *   hands back the last category this query loaded, as a snapshot that doesn't refresh until
 *   the new category arrives.
 *
 * @param {number | null} runId - The comparison run id (from {@link useRunId}).
 * @return {ResultsRun} The run to render, the category state, and the switch/refresh state.
 */
export function useResultsRun(runId: number | null): ResultsRun {
  const [state, setState] = useState<{
    runId: number | null;
    rescat: string | null;
    fallback: string | null;
  }>({ runId, rescat: null, fallback: null });
  // A different run starts at All Rescats — reset during render (React's "adjust state when a
  // prop changes" pattern), and read through the run guard so even this render is unscoped.
  if (state.runId !== runId) setState({ runId, rescat: null, fallback: null });
  const ownState = state.runId === runId;
  const rescat = ownState ? state.rescat : null;
  const fallbackRescat = ownState ? state.fallback : null;

  const query = useComparisonRun(runId, rescat, { subscribe: false });
  const matchedRun = query.data?.id === runId ? query.data : undefined;

  const needsFallback = !matchedRun && rescat !== fallbackRescat;
  const fallbackQuery = useQuery<RunResponse, Error>({
    ...runQueryOptions(runId, fallbackRescat),
    enabled: runId !== null && needsFallback,
  });
  const fallbackRun =
    needsFallback && fallbackQuery.data?.id === runId
      ? fallbackQuery.data
      : undefined;

  const run = matchedRun ?? fallbackRun;
  const shownRescat = run ? run.rescat : null;
  const onSelectedCategory = run !== undefined && shownRescat === rescat;
  useRunUpdates(runId, run);

  return {
    run,
    query,
    rescat,
    setRescat: (next) =>
      setState({ runId, rescat: next, fallback: shownRescat }),
    isRescatPending: !!run && !onSelectedCategory && !query.isError,
    rescatError:
      !!run && !onSelectedCategory && query.isError ? query.error : null,
    refreshError: onSelectedCategory && query.isError ? query.error : null,
  };
}

/**
 * React Query options for a group's factor-level drill-down waterfall, keyed by
 * (run, group, reserve category). Extracted from {@link useGroupFactors} so both the chart's
 * single active drill and the Summary Table's `useQueries` (its many expanded rows) share ONE
 * query definition — identical keys mean one cache entry per (run, group, category), fetched
 * once and reused across both views. Enabled only when both ids are present.
 *
 * Slots Removed / Added drill straight to their slots, which leave out the $0 ones unless
 * `includeUnchanged`; for any other group the flag means nothing, so it never splits the cache.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {string | null} groupKey - The group to decompose, or null when not drilled.
 * @param {string | null} [rescat] - The reserve category to scope to, or null for all.
 * @param {boolean} [includeUnchanged] - Keep the slots that didn't change (Slots Removed /
 *   Added only).
 * @return {object} The React Query options (queryKey, queryFn, enabled, staleTime).
 */
export function groupFactorsQueryOptions(
  runId: number | null,
  groupKey: string | null,
  rescat: string | null = null,
  includeUnchanged = false
) {
  const includeUnchangedSlots =
    includeUnchanged && groupKey !== null && isSlotCoverageKey(groupKey);
  return {
    queryKey: [
      GROUP_FACTORS_QUERY_KEY,
      runId,
      groupKey,
      rescat,
      includeUnchangedSlots,
    ],
    queryFn: ({ signal }: QueryFunctionContext) =>
      api
        .get(
          `packages/eval_comparison/runs/${runId}/groups/${groupKey}/factors${drillQuery(
            rescat,
            includeUnchangedSlots
          )}`,
          { signal }
        )
        .json<DrilldownWaterfallResponse>(),
    enabled: runId !== null && groupKey !== null,
    staleTime: 300_000,
  };
}

/**
 * React Query options for a factor's per-slot drill-down waterfall (the chart's leaf level),
 * keyed by (run, factor, reserve category, $0 slots shown) — each category re-bases the slots
 * server-side, and the $0 slots are left out unless shown, so each caches independently. Enabled
 * only when both ids are present.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {string | null} factorKey - The factor to decompose, or null when not drilled.
 * @param {string | null} [rescat] - The reserve category to scope to, or null for all.
 * @param {boolean} [includeUnchanged] - Keep the slots that didn't change.
 * @return {object} The React Query options (queryKey, queryFn, enabled, staleTime).
 */
export function factorSlotsQueryOptions(
  runId: number | null,
  factorKey: string | null,
  rescat: string | null = null,
  includeUnchanged = false
) {
  const query = drillQuery(rescat, includeUnchanged);
  return {
    queryKey: [
      SLOT_WATERFALL_QUERY_KEY,
      runId,
      factorKey,
      rescat,
      includeUnchanged,
    ],
    queryFn: ({ signal }: QueryFunctionContext) =>
      api
        .get(
          `packages/eval_comparison/runs/${runId}/factors/${factorKey}/slots${query}`,
          { signal }
        )
        .json<DrilldownWaterfallResponse>(),
    enabled: runId !== null && factorKey !== null,
    staleTime: 300_000,
  };
}

/**
 * A group's factor-level drill-down waterfall, keyed by (run, group, reserve category, and — for
 * Slots Removed / Added — whether $0 slots are shown).
 * Fetches only once a group is drilled (enabled when both ids are present) and is cached, so
 * re-opening a group is instant.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {string | null} groupKey - The group to decompose, or null when not drilled.
 * @param {string | null} [rescat] - The reserve category to scope to, or null for all.
 * @param {boolean} [includeUnchanged] - Keep the $0 slots of Slots Removed / Added.
 * @return {UseQueryResult<DrilldownWaterfallResponse, Error>} The React Query result.
 */
export function useGroupFactors(
  runId: number | null,
  groupKey: string | null,
  rescat: string | null = null,
  includeUnchanged = false
): UseQueryResult<DrilldownWaterfallResponse, Error> {
  return useQuery<DrilldownWaterfallResponse, Error>(
    groupFactorsQueryOptions(runId, groupKey, rescat, includeUnchanged)
  );
}

/**
 * A factor's per-slot drill-down waterfall, keyed by (run, factor, reserve category, $0 slots
 * shown).
 * Fetches only once a factor is drilled (enabled when both ids are present) and is cached,
 * so re-opening a factor is instant.
 *
 * @param {number | null} runId - The comparison run id.
 * @param {string | null} factorKey - The factor to decompose, or null when not drilled.
 * @param {string | null} [rescat] - The reserve category to scope to, or null for all.
 * @param {boolean} [includeUnchanged] - Keep the slots that didn't change.
 * @return {UseQueryResult<DrilldownWaterfallResponse, Error>} The React Query result.
 */
export function useFactorSlots(
  runId: number | null,
  factorKey: string | null,
  rescat: string | null = null,
  includeUnchanged = false
): UseQueryResult<DrilldownWaterfallResponse, Error> {
  return useQuery<DrilldownWaterfallResponse, Error>(
    factorSlotsQueryOptions(runId, factorKey, rescat, includeUnchanged)
  );
}

/**
 * The slot list behind one factor's bar: every slot, biggest change first — the Summary Table's
 * deepest level — or highest to lowest for `order` `value`, the order a drill-down chart reveals in;
 * fetched a page at a time (`fetchNextPage`). The slots that didn't change are left out (and
 * uncounted) unless `includeUnchanged`. Keyed by (run, factor, reserve category, $0 slots shown,
 * order), so a category switch — or the switch that shows $0 slots — starts the list from its
 * first page and each open list pages independently; loaded pages stay cached, so re-opening a row
 * is instant. Whether a next page follows is {@link hasPageAfter}'s check.
 *
 * @param {number} runId - The comparison run id.
 * @param {string} factorKey - The factor (or `slots_removed` / `slots_added`) whose slots to list.
 * @param {string | null} rescat - The reserve category to scope to, or null for all.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Fetch the list (default true); false holds it until asked
 *   for, so a chart can load it only once the user expands it.
 * @param {boolean} [options.includeUnchanged] - List the $0 slots too (default false).
 * @param {SlotListOrder} [options.order] - `impact` biggest change first (default), or `value`
 *   highest to lowest — each its own list.
 * @return {UseInfiniteQueryResult<InfiniteData<SlotRowsPageResponse>, Error>} The paged React Query result.
 */
export function useSlotRows(
  runId: number,
  factorKey: string,
  rescat: string | null,
  {
    enabled = true,
    includeUnchanged = false,
    order = 'impact',
  }: {
    enabled?: boolean;
    includeUnchanged?: boolean;
    order?: SlotListOrder;
  } = {}
): UseInfiniteQueryResult<InfiniteData<SlotRowsPageResponse>, Error> {
  return useInfiniteQuery({
    enabled,
    queryKey: [
      SLOT_ROWS_QUERY_KEY,
      runId,
      factorKey,
      rescat,
      includeUnchanged,
      order,
    ],
    queryFn: ({ pageParam, signal }) =>
      api
        .get(
          `packages/eval_comparison/runs/${runId}/factors/${factorKey}/slot_rows`,
          {
            searchParams: {
              page: pageParam,
              ...(rescat ? { rescat } : {}),
              ...(includeUnchanged ? { include_unchanged: true } : {}),
              ...(order === 'value' ? { order: 'value' } : {}),
            },
            signal,
          }
        )
        .json<SlotRowsPageResponse>(),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      hasPageAfter(last) ? last.page + 1 : undefined,
    staleTime: 300_000,
  });
}

/**
 * A slot drill-down's reveal-as-you-drag (INS-12796). It opens as its capped chart — the biggest
 * movers; the first drag past its end (`revealMore`) switches it to the level's slot list highest to
 * lowest, and each drag past the end draws {@link SLOT_REVEAL_STEP} more of it, at the end. That list
 * is the value-ordered one (`useSlotRows` with `order: 'value'`, one cache entry per run, list,
 * category and $0 switch), apart from the Summary Table's biggest-first list. A page is fetched only
 * when a reveal needs it — the first once a reveal is asked for, each next one a step early, so the
 * following drag rarely waits. How far each level is revealed is kept per level, category and $0
 * switch while this view stays mounted.
 *
 * A reveal waits while the rows it needs are on their way, and after a failed page until
 * `retry`; a drag while it waits on rows nothing is fetching asks for them again. A page asked for a
 * step early that fails shows nothing: the next drag asks for it again. It stops once
 * every listed slot is drawn — the $0 ones only when the page shows them.
 *
 * @param {object} level - The drill level.
 * @param {number} level.runId - The run on screen.
 * @param {string | null} level.levelKey - The level's key, category and $0 switch included; null
 *   at the overview, which reveals nothing.
 * @param {string} level.listKey - The level's slot list: the factor, or Slots Removed / Added.
 * @param {string | null} level.rescat - The category on screen (null = all).
 * @param {boolean} level.includeUnchanged - The page shows the slots that didn't change.
 * @param {WaterfallBar[] | undefined} level.cappedBars - The level's capped chart, once loaded.
 * @return {SlotReveal} What to draw, and the reveal's state and actions.
 */
export function useSlotReveal({
  runId,
  levelKey,
  listKey,
  rescat,
  includeUnchanged,
  cappedBars,
}: {
  runId: number;
  levelKey: string | null;
  listKey: string;
  rescat: string | null;
  includeUnchanged: boolean;
  cappedBars: WaterfallBar[] | undefined;
}): SlotReveal {
  const [revealedByLevel, setRevealedByLevel] = useState<
    ReadonlyMap<string, number>
  >(() => new Map());
  const revealedCount =
    levelKey === null ? 0 : (revealedByLevel.get(levelKey) ?? 0);
  const slotRows = useSlotRows(runId, listKey, rescat, {
    enabled: revealedCount > 0,
    includeUnchanged,
    order: 'value',
  });
  const pages = slotRows.data?.pages;
  const reveal = useMemo(
    () =>
      cappedBars
        ? revealSlotWaterfall(cappedBars, pages ?? [], revealedCount)
        : null,
    [cappedBars, pages, revealedCount]
  );

  // A reveal the rows haven't caught up with yet.
  const waiting =
    revealedCount > 0 &&
    !!reveal &&
    !reveal.complete &&
    reveal.shownCount < reveal.cappedSlotCount + revealedCount;
  // A failure counts only while the reveal is short of rows: a page asked for a step early, or a
  // refresh of rows already in, that fails leaves the reveal to carry on from what it has.
  const failed = waiting && slotRows.isError && !slotRows.isFetching;

  return {
    reveal,
    revealedCount,
    extendable: levelKey !== null && !!reveal && !reveal.complete,
    loading: waiting && slotRows.isFetching,
    failed,
    revealMore: () => {
      if (levelKey === null || !cappedBars || !reveal) return;
      if (reveal.complete || failed) return;
      /**
       * Asks for the list's next page, unless there's none or the list is already fetching — a
       * page, or a refresh of the pages in after a run update — which this never cancels; a later
       * drag asks again if the rows still fall short. Not awaited: the list's own state drives the
       * chart and its status.
       */
      const fetchNext = () => {
        if (slotRows.hasNextPage && !slotRows.isFetching) {
          void slotRows.fetchNextPage();
        }
      };
      if (waiting) {
        // The rows asked for aren't in. Ask for them again rather than wait on nothing: the cached
        // list may have expired and come back as its first page only.
        fetchNext();
        return;
      }
      const next = revealedCount + SLOT_REVEAL_STEP;
      setRevealedByLevel((previous) => new Map(previous).set(levelKey, next));
      if (revealSlotWaterfall(cappedBars, pages ?? [], next).needsNextPage) {
        fetchNext();
      }
    },
    // Repeats the request that failed: the next page, or the list's first page / a refresh of it.
    // Try again never cancels a request already on its way (`cancelRefetch: false`). A refresh
    // retried reads the pages already in; the next drag asks for any more the reveal needs.
    retry: () => {
      if (slotRows.isFetchNextPageError) {
        void slotRows.fetchNextPage({ cancelRefetch: false });
      } else {
        void slotRows.refetch();
      }
    },
  };
}

/**
 * Derives the ordered factor-group rows (with per-group changed counts) from the
 * analysis inventory, and reorders via the factor-order atom.
 *
 * @param {AnalysisResponse | null | undefined} analysis - The comparison analysis
 *   from `useComparisonAnalysis`; null/undefined while it loads or is unavailable.
 * @return {object} The ordered `groups`, the `totalChanged` count, the `iterations`
 *   count, and a `reorder(from, to)` helper.
 */
export function useFactorOrder(analysis: AnalysisResponse | null | undefined) {
  const [order, setOrder] = useAtom(factorGroupOrderAtom);

  // Reconciled so every FACTOR_GROUPS group is always listed exactly once.
  const effectiveOrder = useMemo(
    () => reconcileFactorGroupOrder(order),
    [order]
  );

  // Count the changed factors per group from the analysis inventory.
  const changedByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    (analysis?.factors ?? []).forEach((factor) => {
      if (factor.changed) {
        counts[factor.group] = (counts[factor.group] ?? 0) + 1;
      }
    });
    return counts;
  }, [analysis]);

  const groups = useMemo<FactorGroupRow[]>(
    () =>
      effectiveOrder.map((key) => ({
        key,
        label: FACTOR_GROUPS.find((group) => group.key === key)?.label ?? key,
        changedCount: changedByGroup[key] ?? 0,
      })),
    [effectiveOrder, changedByGroup]
  );

  // Single source: the summary total is exactly the sum of the visible rows.
  // Assumes FACTOR_GROUPS covers every `group` the BE emits — a changed factor in
  // an unlisted group would be dropped from both the rows and this total, so keep
  // FACTOR_GROUPS in sync with FactorDiff's group set.
  const totalChanged = useMemo(
    () => groups.reduce((sum, group) => sum + group.changedCount, 0),
    [groups]
  );

  const reorder = useCallback(
    (from: number, to: number) => {
      setOrder((prev) => {
        const base = reconcileFactorGroupOrder(prev);
        if (from < 0 || from >= base.length || to < 0 || to >= base.length) {
          return prev;
        }
        const next = [...base];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    },
    [setOrder]
  );

  return {
    groups,
    totalChanged,
    // One engine calc per changed factor, plus the Origin baseline.
    iterations: totalChanged + 1,
    reorder,
  };
}
